import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FioriPageHeader } from "@/components/fiori";
import {
  MessageSquare,
  Shield,
  Wrench,
  ArrowRight,
  CheckCircle2,
  Clock,
  Plug,
  Zap,
  Activity,
  Power,
  Puzzle,
} from "lucide-react";

// ============================================================================
// Integration definitions — the marketplace catalog
// ============================================================================

interface IntegrationCard {
  id: string;
  name: string;
  nameKey: string;
  description: string;
  descriptionKey: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  route: string;
  status: "active" | "available" | "coming_soon";
  category: "communication" | "hardware" | "enterprise";
  featureKeys: string[];
}

const INTEGRATION_CATALOG: IntegrationCard[] = [
  {
    id: "communications",
    name: "Communications",
    nameKey: "integrationHub.communications",
    description: "Unified messaging hub for SMS, WhatsApp, and Email. Configure providers (Twilio, SMTP, SendGrid), create templates with dynamic variables, set trigger rules for automated notifications, and track delivery logs across all channels.",
    descriptionKey: "integrationHub.communicationsFullDesc",
    icon: MessageSquare,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-600",
    route: "/integration-hub/communications",
    status: "active",
    category: "communication",
    featureKeys: ["integrationHub.featureSMS", "integrationHub.featureWhatsApp", "integrationHub.featureEmail", "integrationHub.featureHTMLTemplates", "integrationHub.featureTriggerRules", "integrationHub.featureDeliveryLogs"],
  },
  {
    id: "siemens-siport",
    name: "Siemens SiPort",
    nameKey: "integrationHub.siemensSiport",
    description: "Integrate with Siemens SiPort access control system for automated gate management, card validation, and real-time access monitoring.",
    descriptionKey: "integrationHub.siemensSiportDesc",
    icon: Shield,
    iconBg: "bg-teal-500/10",
    iconColor: "text-teal-600",
    route: "",
    status: "coming_soon",
    category: "hardware",
    featureKeys: ["integrationHub.featureGateControl", "integrationHub.featureCardValidation", "integrationHub.featureAccessMonitoring", "integrationHub.featureEventSync"],
  },
  {
    id: "eam",
    name: "Enterprise Asset Management",
    nameKey: "integrationHub.eam",
    description: "Connect with Enterprise Asset Management platforms (IBM Maximo, SAP PM, Infor EAM) for unified asset lifecycle tracking, preventive maintenance scheduling, work order management, and spare parts inventory across all data centre facilities.",
    descriptionKey: "integrationHub.eamDesc",
    icon: Wrench,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-600",
    route: "",
    status: "coming_soon",
    category: "enterprise",
    featureKeys: ["integrationHub.featureAssetLifecycle", "integrationHub.featureWorkOrders", "integrationHub.featurePreventiveMaintenance", "integrationHub.featureSpareParts", "integrationHub.featureComplianceReporting"],
  },
  {
    id: "ai-services",
    name: "AI Services",
    nameKey: "integrationHub.aiServices",
    description: "Advanced AI-powered verification using Claude API. Enable face matching, document validation, anomaly detection, and plate recognition for enhanced security checkpoint operations.",
    descriptionKey: "integrationHub.aiServicesDesc",
    icon: Zap,
    iconBg: "bg-purple-500/10",
    iconColor: "text-purple-600",
    route: "/integration-hub/ai-services",
    status: "active",
    category: "communication",
    featureKeys: ["integrationHub.featureFaceMatching", "integrationHub.featureDocumentValidation", "integrationHub.featureAnomalyDetection", "integrationHub.featurePlateRecognition", "integrationHub.featureClaudeAPI"],
  },
];

// ============================================================================
// MAIN COMPONENT — Integration Hub Landing
// ============================================================================

export default function IntegrationHub() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();

  // Fetch stats for active integrations
  const { data: integrationsList } = trpc.messaging.listIntegrations.useQuery();
  const { data: logStats } = trpc.messaging.getLogStats.useQuery();

  const activeProviders = integrationsList?.filter(i => i.isEnabled).length || 0;
  const totalMessages = logStats?.totalMessages || 0;

  const categories = [
    { key: "communication", label: t("integrationHub.catCommunication", "Communication & AI") },
    { key: "hardware", label: t("integrationHub.catHardware", "Hardware & Access Control") },
    { key: "enterprise", label: t("integrationHub.catEnterprise", "Enterprise Systems") },
  ];

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      {/* Header - Fiori Style */}
      <FioriPageHeader
        title={t("integrationHub.title", "Integration Hub")}
        subtitle={t("integrationHub.hubSubtitle", "Connect Centre3 with external systems, communication channels, and hardware devices")}
        icon={<Puzzle className="h-5 w-5" />}
      />

      {/* Overview Stats - Fiori Style KPI Cards */}
      <div className="px-0 py-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#E8DCF5]">
              <Plug className="h-5 w-5 text-[#5B2C93]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#2C2C2C]">{INTEGRATION_CATALOG.length}</p>
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("integrationHub.totalIntegrations", "Total Integrations")}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#D1FAE5]">
              <Power className="h-5 w-5 text-[#059669]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#059669]">{INTEGRATION_CATALOG.filter(i => i.status === "active").length}</p>
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("integrationHub.activeIntegrations", "Active Integrations")}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#DBEAFE]">
              <Zap className="h-5 w-5 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#2563EB]">{activeProviders}</p>
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("integrationHub.connectedProviders", "Connected Providers")}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-[#E0E0E0] p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#E8DCF5]">
              <Activity className="h-5 w-5 text-[#5B2C93]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#2C2C2C]">{totalMessages}</p>
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">{t("integrationHub.messagesSent", "Messages Sent")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Integration Cards by Category - Fiori Style */}
      <div className="px-0 pb-6 space-y-6">
        {categories.map(cat => {
          const catIntegrations = INTEGRATION_CATALOG.filter(i => i.category === cat.key);
          if (catIntegrations.length === 0) return null;

          return (
            <div key={cat.key}>
              <h2 className="text-sm font-semibold text-[#2C2C2C] uppercase tracking-wide mb-3">{cat.label}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {catIntegrations.map(integration => (
                  <IntegrationCardComponent
                    key={integration.id}
                    integration={integration}
                    onNavigate={() => {
                      if (integration.route) {
                        setLocation(integration.route);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Integration Card Component
// ============================================================================

function IntegrationCardComponent({
  integration,
  onNavigate,
}: {
  integration: IntegrationCard;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const Icon = integration.icon;

  const statusBadge = () => {
    switch (integration.status) {
      case "active":
        return (
          <Badge className="bg-[#D1FAE5] text-[#059669] border-[#059669]/20 text-xs gap-1 rounded font-medium">
            <CheckCircle2 className="h-3 w-3" />
            {t("integrationHub.statusActive", "Active")}
          </Badge>
        );
      case "available":
        return (
          <Badge className="border-[#E0E0E0] text-[#6B6B6B] bg-white text-xs gap-1 rounded font-medium">
            <Power className="h-3 w-3" />
            {t("integrationHub.statusAvailable", "Available")}
          </Badge>
        );
      case "coming_soon":
        return (
          <Badge className="bg-[#F5F5F5] text-[#6B6B6B] text-xs gap-1 rounded font-medium">
            <Clock className="h-3 w-3" />
            {t("integrationHub.statusComingSoon", "Coming Soon")}
          </Badge>
        );
    }
  };

  return (
    <Card
      className={`group transition-all duration-200 ${
        integration.status !== "coming_soon"
          ? "hover:shadow-md hover:border-primary/30 cursor-pointer"
          : "opacity-75"
      }`}
      onClick={integration.status !== "coming_soon" ? onNavigate : undefined}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl ${integration.iconBg}`}>
            <Icon className={`h-7 w-7 ${integration.iconColor}`} />
          </div>
          {statusBadge()}
        </div>

        <h3 className="text-base font-semibold text-[#2C2C2C] mb-1">
          {t(integration.nameKey, integration.name)}
        </h3>
        <p className="text-sm text-[#6B6B6B] mb-4 line-clamp-3">
          {t(integration.descriptionKey, integration.description)}
        </p>

        {/* Feature Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {integration.featureKeys.map(key => (
            <span
              key={key}
              className="text-xs px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[#6B6B6B]"
            >
              {t(key)}
            </span>
          ))}
        </div>

        {/* Action */}
        {integration.status !== "coming_soon" ? (
          <Button
            variant="ghost"
            className="w-full justify-between text-[#5B2C93] hover:text-[#5B2C93] hover:bg-[#E8DCF5]"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate();
            }}
          >
            {t("integrationHub.configure", "Configure")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        ) : (
          <div className="text-center py-2 text-sm text-[#9CA3AF]">
            {t("integrationHub.comingSoonNote", "Available in a future release")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
